import React, { useState, useEffect, useRef } from 'react';
import useYjsDocument from '../../../../shared/hooks/useYjsDocument';
import yjsDocumentService from '../../../../infrastructure/sync/YjsDocumentService';
import './CollaborativeEditor.css';

/**
 * CollaborativeEditor - A proof of concept component for Y.js integration
 * Demonstrates real-time collaborative editing using Y.js
 */
const CollaborativeEditor = () => {
  // Get workspaceId from URL parameters or use a default
  const [workspaceId, setWorkspaceId] = useState('demo-doc');
  const [newWorkspaceId, setNewWorkspaceId] = useState('');
  const [filePath, setFilePath] = useState('content');
  const [newFilePath, setNewFilePath] = useState('');
  const [eventLogs, setEventLogs] = useState([]);
  const [showLogs, setShowLogs] = useState(true);
  const [filterType, setFilterType] = useState('all');
  const [files, setFiles] = useState([]);
  const logsEndRef = useRef(null);

  const {
    content: body,
    metadata,
    updateContent: updateBody,
    updateMetadata,
    isConnected,
    isSynced,
    connectedUsers,
    error
  } = useYjsDocument(workspaceId, filePath);

  // Add event to logs with timestamp
  const addEventLog = (event) => {
    const timestamp = new Date().toLocaleTimeString();
    setEventLogs(prev => {
      // Keep only the latest 100 logs to prevent memory issues
      const newLogs = [...prev, { timestamp, event }];
      if (newLogs.length > 100) {
        return newLogs.slice(-100);
      }
      return newLogs;
    });
  };

  // Auto-scroll to bottom of logs
  useEffect(() => {
    if (logsEndRef.current && showLogs) {
      logsEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [eventLogs, showLogs]);

  // Subscribe to workspace events
  useEffect(() => {
    // Subscribe to workspace-level events
    const unsubscribeDoc = yjsDocumentService.subscribeToDocumentEvents(
      workspaceId,
      (event) => {
        addEventLog(event);
      }
    );

    // Subscribe to awareness events
    const unsubscribeAwareness = yjsDocumentService.subscribeToAwarenessEvents(
      workspaceId,
      (event) => {
        addEventLog(event);
      }
    );

    // Clean up subscriptions when component unmounts or workspaceId changes
    return () => {
      unsubscribeDoc();
      unsubscribeAwareness();
    };
  }, [workspaceId]);

  // Subscribe to file list updates
  useEffect(() => {
    const unsubscribe = yjsDocumentService.subscribeToFileList(workspaceId, (fileList) => {
      setFiles(fileList);
    });
    return unsubscribe;
  }, [workspaceId]);

  // Update page title when filePath changes
  useEffect(() => {
    document.title = `${filePath || 'Untitled'} - Collaborative Editor`;
  }, [filePath]);

  // Handle changing to a different workspace
  const handleSwitchWorkspace = (e) => {
    e.preventDefault();
    if (newWorkspaceId.trim()) {
      setWorkspaceId(newWorkspaceId.trim());
      setNewWorkspaceId('');
      // Clear event logs when switching workspaces
      setEventLogs([]);
    }
  };

  // Handle changing to a different file path
  const handleSwitchFilePath = (e) => {
    e.preventDefault();
    if (newFilePath.trim()) {
      setFilePath(newFilePath.trim());
      // File will be added to list automatically when connectToDocument is called
      setNewFilePath('');
    }
  };

  const handleSelectFile = (selectedFile) => {
    setFilePath(selectedFile);
  };

  const handleDeleteFile = (fileToDelete) => {
    if (fileToDelete === filePath) {
      alert('Cannot delete the currently open file. Please switch to another file first.');
      return;
    }
    yjsDocumentService.deleteFileFromList(workspaceId, fileToDelete);
  };

  // Filter logs by type
  const filteredLogs = eventLogs.filter(log => {
    if (filterType === 'all') return true;
    return log.event.type === filterType;
  });

  // Format event for display
  const formatEventForDisplay = (event) => {
    switch (event.type) {
      case 'document-update':
        return `Document update - ${event.update.length} bytes, origin: ${event.origin}`;
      case 'subdocs-change':
        return `Subdocs change - Added: ${event.added.length}, Removed: ${event.removed.length}`;
      case 'awareness-change':
        return `Awareness change - ${event.users.length} users, Changes: +${event.changes.added.length} ~${event.changes.updated.length} -${event.changes.removed.length}`;
      case 'text-update':
        return `Text update`;
      default:
        return `Unknown event type: ${event.type} - ${JSON.stringify(event).substring(0, 100)}...`;
    }
  };

  // Get CSS class for event type
  const getEventTypeClass = (type) => {
    switch (type) {
      case 'document-update': return 'event-document';
      case 'subdocs-change': return 'event-subdoc';
      case 'awareness-change': return 'event-awareness';
      case 'text-update': return 'event-text';
      default: return 'event-unknown';
    }
  };

  return (
    <div className="collaborative-editor">
      <div className="editor-header">
        <h1>Collaborative Editor</h1>
        <div className="connection-status">
          {error ? (
            <span className="error-message">{error}</span>
          ) : (
            <>
              <span className={`status-indicator ${isConnected ? 'connected' : 'disconnected'}`}>
                {isConnected ? '● Connected' : '○ Disconnected'}
              </span>
              <span className={`sync-indicator ${isSynced ? 'synced' : 'syncing'}`}>
                {isSynced ? '(Synced)' : '(Syncing...)'}
              </span>
            </>
          )}
        </div>
      </div>

      <div className="document-switcher">
        <form onSubmit={handleSwitchWorkspace}>
          <input
            type="text"
            value={newWorkspaceId}
            onChange={(e) => setNewWorkspaceId(e.target.value)}
            placeholder="Enter workspace ID"
          />
          <button type="submit">Switch Workspace</button>
        </form>
        <div className="current-document">
          Current Workspace: <strong>{workspaceId}</strong>
        </div>
      </div>

      <div className="document-switcher">
        <form onSubmit={handleSwitchFilePath}>
          <input
            type="text"
            value={newFilePath}
            onChange={(e) => setNewFilePath(e.target.value)}
            placeholder="Enter file path"
          />
          <button type="submit">Switch File</button>
        </form>
        <div className="current-document">
          Current File Path: <strong>{filePath}</strong>
        </div>
      </div>

      <div className="editor-layout">
        <div className="workspace-files">
          {files.map(file => (
            <div key={file} className="file-item">
              <span onClick={() => handleSelectFile(file)}>{file}</span>
              <button
                className="delete-file-btn"
                onClick={() => handleDeleteFile(file)}
                title="Delete file"
              >
                ✖
              </button>
            </div>
          ))}
        </div>
        <div className="editor-container">
          <div className="body-container">
            <textarea
              value={body}
              onChange={(e) => updateBody(e.target.value)}
              placeholder={`Start typing in ${filePath} field...`}
              className="document-body-input"
            />
          </div>
        </div>
      </div>

      <div className="metadata-container">
        <h3>File Metadata</h3>
        <div className="metadata-fields">
          <div className="metadata-field">
            <label>Title:</label>
            <input
              type="text"
              value={metadata.title || ''}
              onChange={(e) => updateMetadata({ title: e.target.value })}
              placeholder="File title"
            />
          </div>
          <div className="metadata-field">
            <label>Description:</label>
            <input
              type="text"
              value={metadata.description || ''}
              onChange={(e) => updateMetadata({ description: e.target.value })}
              placeholder="File description"
            />
          </div>
          <div className="metadata-field">
            <label>Last Modified:</label>
            <span>{metadata.lastModified || 'Never'}</span>
          </div>
        </div>
      </div>

      <div className="collaboration-info">
        <h3>Connected Users ({connectedUsers.length})</h3>
        <ul className="users-list">
          {connectedUsers.map((user) => (
            <li key={user.clientId}>
              <span className="user-badge">{user.name || 'Anonymous'}</span>
            </li>
          ))}
        </ul>
      </div>
      
      <div className="events-section">
        <div className="events-header">
          <h3>Y.js Events</h3>
          <div className="events-controls">
            <button 
              onClick={() => setShowLogs(!showLogs)} 
              className="toggle-logs-btn"
            >
              {showLogs ? 'Hide Logs' : 'Show Logs'}
            </button>
            
            <select 
              value={filterType} 
              onChange={(e) => setFilterType(e.target.value)}
              className="filter-select"
            >
              <option value="all">All Events</option>
              <option value="document-update">Document Updates</option>
              <option value="subdocs-change">Subdoc Changes</option>
              <option value="awareness-change">Awareness Changes</option>
              <option value="text-update">Text Updates</option>
            </select>
            
            <button 
              onClick={() => setEventLogs([])} 
              className="clear-logs-btn"
            >
              Clear Logs
            </button>
          </div>
        </div>
        
        {showLogs && (
          <div className="events-log">
            {filteredLogs.length === 0 ? (
              <div className="no-events">No events recorded yet</div>
            ) : (
              filteredLogs.map((log, index) => (
                <div 
                  key={`${log.timestamp}-${index}`}
                  className={`event-entry ${getEventTypeClass(log.event.type)}`}
                >
                  <span className="event-timestamp">{log.timestamp}</span>
                  <span className="event-type">{log.event.type}</span>
                  <span className="event-description">
                    {formatEventForDisplay(log.event)}
                  </span>
                </div>
              ))
            )}
            <div ref={logsEndRef} />
          </div>
        )}
      </div>
    </div>
  );
};

export default CollaborativeEditor;